import * as Header from "app/header/components/Header"
import { PropsWithChildren, useMemo } from "react"
import Layout from "./Layout"
import Image from "next/image"
import { GetLessonResult } from "app/slides/queries/getLesson"
import Group from "ui/Group"
import RoundedButton from "ui/RoundedButton"
import getLinkProps from "../utils/getLinkProps"
import { AnalyticsObject } from "app/analytics/utils/generateAnalyticsObject"
import { Value } from "@stringtale/react"
import styled from "@emotion/styled"
import { css, Global } from "@emotion/react"

const FreeTrialButtonContainer = styled(Group)`
  margin-left: 20px;
`

export default function PublicLayout(
  props: PropsWithChildren<{
    title?: string | null
    lesson?: GetLessonResult
    forceShowTrialButton?: boolean
    analyticsObject?: Partial<AnalyticsObject>
  }>
) {
  const { lesson } = props

  const analyticsObject = useMemo<Partial<AnalyticsObject>>(() => {
    return {
      ...props.analyticsObject,
      isPublicPage: true,
      userType: "PUBLIC",
    }
  }, [props.analyticsObject])

  return (
    <Layout {...props} analyticsObject={analyticsObject}>
      <Global
        styles={css`
          body {
            color: var(--color-neutraal-900);
          }
        `}
      />
      <div className="text-neutraal-900">
        <Header.Root>
          <Header.Content>
            <Group gap="40px" grow>
              <Header.LogoRoot>
                <a
                  href="https://www.123zing.nl/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Image
                    src={"/123Zing_logo.svg"}
                    width={72}
                    height={55}
                    alt="Logo"
                  />
                </a>
              </Header.LogoRoot>
              <Group grow align="center" justify="space-between" gap="20px">
                {(lesson?.publicButton1Label && lesson?.publicButton1Link) ||
                (lesson?.publicButton2Label && lesson?.publicButton2Link) ? (
                  <Group align="center" gap="8px">
                    {lesson.publicButton1Label && lesson.publicButton1Link ? (
                      <RoundedButton
                        {...getLinkProps(lesson.publicButton1Link, "BLANK")}
                      >
                        {lesson.publicButton1Label}
                      </RoundedButton>
                    ) : null}
                    {lesson.publicButton2Label && lesson.publicButton2Link ? (
                      <RoundedButton
                        {...getLinkProps(lesson.publicButton2Link, "BLANK")}
                      >
                        {lesson.publicButton2Label}
                      </RoundedButton>
                    ) : null}
                  </Group>
                ) : null}
                {lesson?.publicButtons.map((group) => (
                  <Group align="center" gap="8px">
                    {group.buttons.map(
                      ({ label, id, target, link, ...button }) => (
                        <RoundedButton
                          {...button}
                          key={id}
                          {...getLinkProps(link, target)}
                        >
                          {label}
                        </RoundedButton>
                      )
                    )}
                  </Group>
                ))}
              </Group>
            </Group>
            {(lesson?.isPublicFreeTrialButtonVisible ||
              props.forceShowTrialButton) && (
              <FreeTrialButtonContainer align="center">
                <RoundedButton
                  variant="filled"
                  {...getLinkProps(
                    "https://www.123zing.nl/gratis-proefabonnement",
                    "BLANK"
                  )}
                >
                  <Value name="apps.web.src.core.layouts.publiclayout.probeer_123zing_2_maanden_gratis">
                    Probeer 123ZING 2 maanden gratis!
                  </Value>
                </RoundedButton>
              </FreeTrialButtonContainer>
            )}
          </Header.Content>
        </Header.Root>
        {props.children}
      </div>
    </Layout>
  )
}
